
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { toast } from 'sonner';

type UserRole = 'admin' | 'user' | null;

interface AuthContextType {
  session: Session | null;
  user: User | null;
  role: UserRole;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any | null }>;
  signUp: (email: string, password: string) => Promise<{ error: any | null }>;
  signOut: () => Promise<void>;
  checkIsAdmin: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Ensure admin@example.com has admin role using user_roles table
  const ensureAdminUser = async (userId: string) => {
    try {
      const { data: existingRole } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle();
      
      if (!existingRole) {
        const { error: insertError } = await supabase
          .from('user_roles')
          .insert({ user_id: userId, role: 'admin' });
        
        if (insertError) {
          console.error('Error assigning admin role:', insertError);
        } else {
          console.log('Admin role assigned successfully');
          toast.success('Admin permissions granted');
        }
      }
    } catch (error) {
      console.error('Error ensuring admin user:', error);
    }
  };

  // Check if user is admin by querying the user_roles table
  const checkIsAdmin = async (): Promise<boolean> => {
    try {
      if (!user) {
        console.log('checkIsAdmin: No user found');
        return false;
      }
      
      console.log('Checking admin status for user:', user.id);
      
      const { data: userRole, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      
      if (error) {
        console.error('Error checking user role:', error);
        return false;
      }
      
      const isAdmin = userRole?.role === 'admin';
      console.log('User is admin:', isAdmin);
      
      // If admin@example.com, ensure they have admin role
      if (!isAdmin && user.email === 'admin@example.com') {
        await ensureAdminUser(user.id);
        return true;
      }
      
      return isAdmin;
    } catch (err) {
      console.error('Admin check failed:', err);
      return false;
    }
  };

  useEffect(() => {
    const getSession = async () => {
      setIsLoading(true);
      
      try {
        console.log('Getting session...');
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          console.log('Session found, user:', session.user.id);
          setSession(session);
          setUser(session.user);
          
          // Check if user is admin
          const isAdmin = await checkIsAdmin();
          console.log('User is admin:', isAdmin);
          setRole(isAdmin ? 'admin' : 'user');
        } else {
          console.log('No session found');
        }
        
        // Check admin status will handle ensuring admin role
      } catch (error) {
        console.error('Session retrieval error:', error);
        toast.error('Error retrieving your session');
      } finally {
        setIsLoading(false);
      }
    };

    getSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (eventType, session) => {
      console.log('Auth state changed:', eventType, session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        const isAdmin = await checkIsAdmin();
        console.log('User is admin (from auth change):', isAdmin);
        setRole(isAdmin ? 'admin' : 'user');
      } else {
        setRole(null);
      }
      
      setIsLoading(false);
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      console.log('Signing in user:', email);
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (!error) {
        console.log('Sign in successful');
        toast.success('Signed in successfully');
        
        // We'll check admin status and redirect in the auth state change listener
        if (email === 'admin@example.com') {
          navigate('/admin');
        } else {
          navigate('/');
        }
      } else {
        console.error('Sign in error:', error);
        toast.error(`Sign in failed: ${error.message}`);
      }
      
      return { error };
    } catch (error) {
      console.error('Sign in exception:', error);
      toast.error('An unexpected error occurred during sign in');
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      console.log('Signing up user:', email);
      setIsLoading(true);
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (!error) {
        console.log('Sign up successful. User may need to verify email.');
        toast.success('Sign up successful! Please check your email for verification.');
      } else {
        console.error('Sign up error:', error);
        toast.error(`Sign up failed: ${error.message}`);
      }
      
      return { error };
    } catch (error) {
      console.error('Sign up exception:', error);
      toast.error('An unexpected error occurred during sign up');
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      console.log('Signing out user');
      setIsLoading(true);
      await supabase.auth.signOut();
      toast.success('Signed out successfully');
      navigate('/auth');
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Error signing out');
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    session,
    user,
    role,
    isLoading,
    signIn,
    signUp,
    signOut,
    checkIsAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
